<script setup lang="ts">
import type { Comment } from "@/models"
import { formatDistanceToNow } from "date-fns"
import { Button, ReadMore } from "@/components/ui"
import { useTranslation } from "@/composables"

withDefaults(
  defineProps<{
    comment?: Comment
    isLoading?: boolean
  }>(),
  { comment: undefined, isLoading: false },
)

const emit = defineEmits<{
  (e: "delete", comment: Comment): void
  (e: "flag", comment: Comment): void
}>()

const { t } = useTranslation()

function getTimeAgo(timestamp: Date): string {
  return formatDistanceToNow(timestamp, { addSuffix: true })
}
</script>

<template>
  <div>
    <!-- skeleton loader -->
    <div v-if="isLoading" class="font-montserrat text-sm md:text-base flex flex-col gap-1 bg-white">
      <div class="flex flex-col gap-2 mb-2">
        <div class="gpx-skeleton h-4 w-full" />
        <div class="gpx-skeleton h-4 w-full" />
        <div class="gpx-skeleton h-4 w-5/6" />
      </div>
      <div class="flex justify-between items-end gap-2">
        <div class="flex flex-col gap-1">
          <div class="gpx-skeleton h-4 w-24" />
          <div class="gpx-skeleton h-3 w-20" />
        </div>
      </div>
    </div>

    <!-- comment tile -->
    <div v-else class="font-montserrat text-sm md:text-base flex flex-col gap-1 bg-white">
      <div><ReadMore :content="comment?.content" :lines="4" /></div>
      <div class="flex justify-between items-end gap-2">
        <div>
          <div class="text-primary italic font-semibold">
            {{ comment?.createdBy }}
          </div>
          <div v-if="comment?.dateCreated" class="text-label text-xs md:text-sm">
            {{ getTimeAgo(comment.dateCreated) }}
          </div>
        </div>
        <div class="flex gap-2">
          <Button
            v-if="comment?.isPrivileged"
            prefix-icon="Trash"
            variant="flat"
            class="p-0! m-0!"
            data-test="delete-comment"
            :aria-label="t('comment_drawer.comment_tile_delete')"
            @click.prevent="() => emit('delete', comment!)"
          />
          <Button
            prefix-icon="Flag"
            variant="flat"
            class="p-0! m-0!"
            data-test="flag-comment"
            :aria-label="t('comment_drawer.comment_tile_flag')"
            @click.prevent="() => emit('flag', comment!)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
